import { cva } from 'class-variance-authority'
import * as React from 'react'
import { type ReactNode, useRef, useState, useEffect } from 'react'

import { cn } from '#app/shared/lib/misc.js'

export const inputContainerClassName = `
  relative h-11 w-full cursor-text rounded-[4px] border border-input
  hover:border-foreground-muted focus:border-2
`

export const inputCommonClassName = `
  flex w-full px-3 py-2 text-sm outline-none
   file:bg-transparent file:text-sm file:font-medium
  placeholder:text-foreground-muted focus-visible:outline-none
   disabled:cursor-not-allowed
  disabled:opacity-50
  peer block h-full w-full appearance-none rounded-[4px]
  bg-white py-3 pl-5 text-sm text-foreground focus:ring-0 transition-all duration-200
`

const inputContainerVariants = cva(inputContainerClassName, {
  variants: {
    variant: {
      outlined: '',
      'outlined-label': '',
      filled: 'border-none',
      'filled-dark': 'border-none',
      'bottom-line': 'rounded-none border-b border-l-0 border-r-0 border-t-0',
      'semi-transparent': 'border-none',
    },
  },
  defaultVariants: {
    variant: 'outlined',
  },
})

const inputVariants = cva(inputCommonClassName, {
  variants: {
    variant: {
      outlined: '',
      'outlined-label': '',
      filled: '',
      'filled-dark': `
        border border-[#FFF]/10 bg-purple-650 text-purple-50

        focus-visible:border-[#FFF]/30

        hover:border-[#FFF]/30

        placeholder:text-purple-400
      `,
      'bottom-line': `
        bg-transparent text-white

        placeholder:text-primary-100
      `,
      'semi-transparent': `
        border border-[rgba(255,255,255,0.24)] bg-[rgba(255,255,255,0.07)]
        text-white

        focus-visible:bg-[rgba(255,255,255,0.10)]

        hover:bg-[rgba(255,255,255,0.10)]
      `,
    },
  },
  defaultVariants: {
    variant: 'outlined',
  },
})

export type InputVariant =
  | 'outlined'
  | 'outlined-label'
  | 'filled'
  | 'filled-dark'
  | 'bottom-line'
  | 'semi-transparent'

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  variant?: InputVariant
  size?: InputSize
  inputClassName?: string
}

export interface InputLabelPlaceholderProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  rightIcon?: ReactNode
  leftIcon?: ReactNode
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      inputClassName,
      type,
      placeholder,
      id,
      value: propValue,
      defaultValue,
      leftIcon,
      rightIcon,
      variant,
      onChange,
      size = '55px',
      ...props
    },
    ref,
  ) => {
    const [value, setValue] = useState<any>(propValue || defaultValue)
    const sizeClassName = inputSizeOptions[size]

    useEffect(() => {
      setValue(propValue || defaultValue)
    }, [propValue, defaultValue])

    const handleChange = (event: any) => {
      if (onChange) {
        onChange(event)
      } else {
        setValue(event.target.value)
      }
    }

    const inputRef = useRef<HTMLInputElement | null>(null)
    const [isFocused, setIsFocused] = useState(false)

    const handleFocus = (event: any) => {
      props.onFocus && props.onFocus(event)
      setIsFocused(true)
    }

    const handleBlur = (event: any) => {
      props.onBlur && props.onBlur(event)
      setIsFocused(false)
    }

    const Comp = variant === 'outlined-label' ? 'fieldset' : 'div'

    return (
      <Comp
        className={cn(
          inputContainerVariants({ variant }),
          sizeClassName,
          props.readOnly && '!cursor-auto !border !border-input',
          isFocused && 'border-2 border-foreground',
          // isFocused && variant === 'outlined' && 'border-secondary',
          className,
        )}
      >
        <input
          ref={(el) => {
            // Assign the input ref to the provided ref
            if (ref) {
              if (typeof ref === 'function') {
                ref(el)
              } else if (ref.hasOwnProperty('current')) {
                // @ts-ignore
                ref.current = el
              }
            }
            inputRef.current = el
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          type={type}
          id={id ?? placeholder}
          className={cn(
            inputVariants({ variant }),
            leftIcon && 'pl-[52px]',
            inputClassName,
          )}
          placeholder={
            (variant !== 'outlined-label' && placeholder) || undefined
          }
          name={`input-${placeholder}`}
          value={value}
          onChange={handleChange}
          {...props}
        />
        {variant === 'outlined-label' && (
          <legend
            className={`
              invisible h-[5px] px-1 text-xs

              ${value || isFocused ? 'block' : `hidden`}
            `}
          >
            {placeholder}
          </legend>
        )}
        {leftIcon && (
          <div
            className={`
              absolute left-4 top-0 flex h-full cursor-text items-center
              text-foreground-muted
            `}
            onClick={() => inputRef.current!.focus()}
            onKeyDown={() => inputRef.current!.focus()}
            role="button"
            tabIndex={0}
          >
            {leftIcon}
          </div>
        )}
        {variant === 'outlined-label' && (
          <label
            htmlFor={id ?? placeholder}
            className={getOutlineLabelStyles({
              leftIcon,
              value,
              open: isFocused,
              className: 'mb-0 cursor-text',
            })}
          >
            {placeholder}
          </label>
        )}
        {rightIcon && (
          <div
            className={`
              absolute right-4 top-0 flex h-full cursor-text items-center
              text-foreground-muted
            `}
            onClick={() => inputRef.current!.focus()}
            onKeyDown={() => inputRef.current!.focus()}
            role="button"
            tabIndex={0}
          >
            {rightIcon}
          </div>
        )}
      </Comp>
    )
  },
)

export type InputSize = '24px' | '32px' | '40px' | '48px' | '55px' | '64px'

export const inputSizeOptions: Record<InputSize, string> = {
  '24px': 'h-[24px]',
  '32px': 'h-[32px]',
  '40px': 'h-[40px]',
  '48px': 'h-[48px]',
  '55px': 'h-[55px]',
  '64px': 'h-[64px]',
}

export const getOutlineLabelStyles = ({
  leftIcon,
  value,
  open,
  className,
}: {
  leftIcon: ReactNode
  value: any
  open: boolean
  className?: string
}) => {
  return cn(
    `
      absolute bottom-0 top-0 flex origin-top-left translate-y-0 scale-100
      items-center font-normal text-foreground-muted duration-100
    `,
    leftIcon ? 'left-10' : 'left-5',
    (value || open) &&
      'left-1 top-0 block -translate-y-3 scale-75 font-semibold text-foreground',
    value && !open && '!text-foreground-muted',
    className,
  )
}

export { Input }
